import type { CalendarEvent, CalendarEventColor, CalendarView } from './types'

export interface EventColorStyles {
    root: string
    label: string
    time: string
    dot: string
}

export const CALENDAR_COLOR_MAP: Record<CalendarEventColor, EventColorStyles> = {
    gray: {
        root: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 ring-1 ring-inset ring-zinc-200 dark:ring-zinc-700 hover:bg-zinc-200/80 dark:hover:bg-zinc-750',
        label: 'text-zinc-800 dark:text-zinc-100 font-semibold',
        time: 'text-zinc-500 dark:text-zinc-400',
        dot: 'bg-zinc-500',
    },
    brand: {
        root: 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-200 ring-1 ring-inset ring-purple-200 dark:ring-purple-800/60 hover:bg-purple-100/80 dark:hover:bg-purple-900/60',
        label: 'text-purple-800 dark:text-purple-100 font-semibold',
        time: 'text-purple-600 dark:text-purple-300',
        dot: 'bg-purple-600',
    },
    green: {
        root: 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-200 ring-1 ring-inset ring-emerald-200 dark:ring-emerald-800/60 hover:bg-emerald-100/80 dark:hover:bg-emerald-900/60',
        label: 'text-emerald-800 dark:text-emerald-100 font-semibold',
        time: 'text-emerald-600 dark:text-emerald-300',
        dot: 'bg-emerald-600',
    },
    blue: {
        root: 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-200 ring-1 ring-inset ring-blue-200 dark:ring-blue-800/60 hover:bg-blue-100/80 dark:hover:bg-blue-900/60',
        label: 'text-blue-800 dark:text-blue-100 font-semibold',
        time: 'text-blue-600 dark:text-blue-300',
        dot: 'bg-blue-600',
    },
    indigo: {
        root: 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-200 ring-1 ring-inset ring-indigo-200 dark:ring-indigo-800/60 hover:bg-indigo-100/80 dark:hover:bg-indigo-900/60',
        label: 'text-indigo-800 dark:text-indigo-100 font-semibold',
        time: 'text-indigo-600 dark:text-indigo-300',
        dot: 'bg-indigo-600',
    },
    purple: {
        root: 'bg-violet-50 dark:bg-violet-950/60 text-violet-700 dark:text-violet-200 ring-1 ring-inset ring-violet-200 dark:ring-violet-800/60 hover:bg-violet-100/80 dark:hover:bg-violet-900/60',
        label: 'text-violet-800 dark:text-violet-100 font-semibold',
        time: 'text-violet-600 dark:text-violet-300',
        dot: 'bg-violet-600',
    },
    pink: {
        root: 'bg-pink-50 dark:bg-pink-950/60 text-pink-700 dark:text-pink-200 ring-1 ring-inset ring-pink-200 dark:ring-pink-800/60 hover:bg-pink-100/80 dark:hover:bg-pink-900/60',
        label: 'text-pink-800 dark:text-pink-100 font-semibold',
        time: 'text-pink-600 dark:text-pink-300',
        dot: 'bg-pink-600',
    },
    orange: {
        root: 'bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200 ring-1 ring-inset ring-amber-200 dark:ring-amber-800/60 hover:bg-amber-100/80 dark:hover:bg-amber-900/60',
        label: 'text-amber-900 dark:text-amber-100 font-semibold',
        time: 'text-amber-700 dark:text-amber-300',
        dot: 'bg-amber-600',
    },
    yellow: {
        root: 'bg-yellow-50 dark:bg-yellow-950/60 text-yellow-800 dark:text-yellow-200 ring-1 ring-inset ring-yellow-200 dark:ring-yellow-800/60 hover:bg-yellow-100/80 dark:hover:bg-yellow-900/60',
        label: 'text-yellow-900 dark:text-yellow-100 font-semibold',
        time: 'text-yellow-700 dark:text-yellow-300',
        dot: 'bg-yellow-500',
    },
}

export const CALENDAR_VIEW_OPTIONS: Array<{
    value: CalendarView
    label: string
    addon: string
}> = [
    { value: 'day', label: 'Day view', addon: '⌘D' },
    { value: 'week', label: 'Week view', addon: '⌘W' },
    { value: 'month', label: 'Month view', addon: '⌘M' },
]

export const WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
export const WEEKDAYS_MINI = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

// Helper to create dates relative to today or anchor date
export function createSampleEvents(anchor: Date = new Date()): CalendarEvent[] {
    const y = anchor.getFullYear()
    const m = anchor.getMonth()
    const d = anchor.getDate()

    return [
        {
            id: 'event-01',
            title: 'Monday standup',
            start: new Date(y, m, d, 9, 0),
            end: new Date(y, m, d, 9, 30),
            color: 'brand',
            dot: true,
            description: 'Weekly team kick-off and priority check-in.',
            location: 'Zoom Meeting',
            meetingUrl: 'https://zoom.us/j/86341969512',
            meetingId: '863 4196 9512',
            attendees: [
                { name: 'Sienna Hewitt', initials: 'SH' },
                { name: 'Ammar Foley', initials: 'AF' },
                { name: 'Pippa Wilkinson', initials: 'PW' },
                { name: 'Olly Schroeder', initials: 'OS' },
            ],
        },
        {
            id: 'event-02',
            title: 'Product demo',
            start: new Date(y, m, d + 1, 10, 30),
            end: new Date(y, m, d + 1, 11, 30),
            color: 'blue',
            description: 'Product demo for the new dashboard and Q&A session.',
            location: 'Zoom Meeting',
            meetingUrl: 'https://zoom.us/j/86341969512',
            meetingId: '863 4196 9512',
            attendees: [
                { name: 'Sienna Hewitt', initials: 'SH' },
                { name: 'Ammar Foley', initials: 'AF' },
                { name: 'Pippa Wilkinson', initials: 'PW' },
                { name: 'Olly Schroeder', initials: 'OS' },
                { name: 'Mathilde Lewis', initials: 'ML' },
                { name: 'Olivia Rhye', initials: 'OR' },
            ],
        },
        {
            id: 'event-03',
            title: 'One-on-one w/ Eva',
            start: new Date(y, m, d + 2, 10, 0),
            end: new Date(y, m, d + 2, 10, 45),
            color: 'pink',
        },
        {
            id: 'event-04',
            title: 'Lunch with Olivia',
            start: new Date(y, m, d + 3, 12, 0),
            end: new Date(y, m, d + 3, 13, 0),
            color: 'green',
            dot: true,
        },
        {
            id: 'event-05',
            title: 'Friday standup',
            start: new Date(y, m, d + 4, 9, 0),
            end: new Date(y, m, d + 4, 9, 30),
            color: 'gray',
        },
        {
            id: 'event-06',
            title: 'House inspection',
            start: new Date(y, m, d - 2, 11, 0),
            end: new Date(y, m, d - 2, 12, 30),
            color: 'orange',
        },
        {
            id: 'event-07',
            title: "Ava's engagement party",
            start: new Date(y, m, d - 1, 13, 0),
            end: new Date(y, m, d - 1, 16, 0),
            color: 'purple',
            dot: true,
        },
        {
            id: 'event-08',
            title: 'Content planning',
            start: new Date(y, m, d, 11, 0),
            end: new Date(y, m, d, 12, 0),
            color: 'blue',
        },
        {
            id: 'event-09',
            title: 'Catch up w/ Alex',
            start: new Date(y, m, d + 1, 14, 30),
            end: new Date(y, m, d + 1, 15, 30),
            color: 'pink',
        },
        {
            id: 'event-10',
            title: 'Deep work',
            start: new Date(y, m, d + 2, 13, 30),
            end: new Date(y, m, d + 2, 15, 30),
            color: 'indigo',
        },
        {
            id: 'event-11',
            title: 'Design sync',
            start: new Date(y, m, d + 2, 16, 0),
            end: new Date(y, m, d + 2, 17, 0),
            color: 'blue',
        },
        {
            id: 'event-12',
            title: 'Team offsite',
            start: new Date(y, m, d + 7, 9, 0),
            end: new Date(y, m, d + 9, 17, 0), // Multi-day event spanning 3 days!
            color: 'green',
            description: 'Annual company retreat and strategic workshops.',
        },
        {
            id: 'event-13',
            title: 'Quarterly review',
            start: new Date(y, m, d + 8, 11, 30),
            end: new Date(y, m, d + 8, 13, 0),
            color: 'orange',
        },
        {
            id: 'event-14',
            title: 'All-hands meeting',
            start: new Date(y, m, d - 4, 16, 0),
            end: new Date(y, m, d - 4, 17, 30),
            color: 'brand',
            dot: true,
        },
    ]
}
